import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { postRequest } from "../../../config/dataApi";
import { toast } from "react-toastify";
import AccountsHeader from "./header";
import ENDPOINTS from "../../../config/apiUrls";
import { updateAdminPassword, updateUserProfile, clearError } from "../../../store/slices/authSlice";
import { useDispatch, useSelector } from "react-redux";

// Formik validation schema
const validationSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string().required("New password is required"),
  confirmPassword: Yup.string()
    .required("Please confirm your new password")
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
});

const UpdatePassword = ({ targetGroupAdmin = false, accountBasePath = "/admin/dashboard/account", showUpdatePassword = true }) => {
  const { error, loading } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Clear any previous errors before making a new request
      dispatch(clearError());
      
      try {
        const result = await dispatch(updateAdminPassword({
          targetGroupAdmin,
          message: "Your password has been updated successfully!", values: {
            currentPassword: values.currentPassword,
            password: values.newPassword,
          }
        }));

        // Check if the action was rejected
        if (updateAdminPassword.rejected.match(result)) {
          // Show error toast directly
          if (result.payload) {
            toast.error(result.payload, {
              position: "top-right",
              autoClose: 5000,
            });
          }
          return;
        }

        // If successful, reset the form
        resetForm();

      } catch (error) {
        console.error("Error updating password:", error);
        toast.error("An unexpected error occurred. Please try again.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    },
  });

  return (
    <div className="section_main_div">
      <h1 className="section_heading">Update Password</h1>
      <AccountsHeader basePath={accountBasePath} showUpdatePassword={showUpdatePassword} />

      <form className="form_div" onSubmit={formik.handleSubmit}>
        <div className="form-card">
          <div className="form-elements">
            <div className="custom-form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                required
              />
              {formik.touched.currentPassword &&
                formik.errors.currentPassword ? (
                <div className="error-text">
                  {formik.errors.currentPassword}
                </div>
              ) : null}
              <hr
                className={
                  formik.touched.currentPassword &&
                    formik.errors.currentPassword
                    ? "error-hr-line"
                    : ""
                }
              />
            </div>
            <div className="custom-form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                required
              />
              {formik.touched.newPassword && formik.errors.newPassword ? (
                <div className="error-text">{formik.errors.newPassword}</div>
              ) : null}
              <hr
                className={
                  formik.touched.newPassword && formik.errors.newPassword
                    ? "error-hr-line"
                    : ""
                }
              />
            </div>
            <div className="custom-form-group">
              <label htmlFor="confirmPassword">Repeat Your New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                required
              />
              {formik.touched.confirmPassword &&
                formik.errors.confirmPassword ? (
                <div className="error-text">
                  {formik.errors.confirmPassword}
                </div>
              ) : null}
              <hr
                className={
                  formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                    ? "error-hr-line"
                    : ""
                }
              />
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="button-div">
            <button type="submit" disabled={formik.isSubmitting || loading}>
              <span>{formik.isSubmitting || loading ? "Updating..." : "Update"}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UpdatePassword;
